import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, ChevronRight, RefreshCw, Bot } from "lucide-react";

import { fetchCoverage, totalsOf, type PublisherCoverage, type MissingEdition } from "../api/coverage";
import { explainError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Spinner } from "../components/Spinner";
import { shortDate, timeSince } from "../lib/format";
import { useBusinessDate } from "../lib/businessDate";

/* ──────────────────────────────────────────────────────────────────────
   /crawler-ops/coverage — expected vs produced for the business date.

   The Today page lists what arrived. This page lists what did NOT, and
   why: per publisher expected / produced / missing, a status chip, the
   last successful date, the login-session state, and — expanded — every
   missing edition with its classification. Attempt errors and open
   alerts for the date sit underneath.
   ────────────────────────────────────────────────────────────────────── */

const STATUS_LABEL: Record<string, string> = {
  ok: "ok", partial: "partial", missing: "missing", pending: "pending",
  dead: "dead", auth_blocked: "auth blocked", scheduled_off: "no issue today", none: "—",
};
const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
  ok:            { bg: "transparent",          fg: "var(--fg-muted)" },
  partial:       { bg: "var(--accent-soft)",   fg: "var(--accent)" },
  missing:       { bg: "var(--accent-soft)",   fg: "var(--accent)" },
  pending:       { bg: "var(--bg-inset)",      fg: "var(--fg-muted)" },
  dead:          { bg: "var(--accent)",        fg: "var(--bg)" },
  auth_blocked:  { bg: "var(--accent)",        fg: "var(--bg)" },
  scheduled_off: { bg: "var(--bg-inset)",      fg: "var(--fg-faint)" },
  none:          { bg: "transparent",          fg: "var(--fg-faint)" },
};
const CLASS_LABEL: Record<string, string> = {
  partial: "partial issue", scheduled_off: "no-publish day", not_activated: "no CityCode",
  auth_blocked: "auth circuit-breaker", pending: "not yet (window open)", new_gap: "new gap", dead: "dead ≥7d",
};

function StatusChip({ status }: { status: string }) {
  const tone = STATUS_TONE[status] ?? STATUS_TONE.none!;
  return (
    <span className="np-chip" style={{ background: tone.bg, color: tone.fg, borderColor: status === "ok" ? "var(--rule)" : tone.bg }}>
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

export function CrawlerCoverage() {
  const { me } = useAuth();
  const { date } = useBusinessDate();
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [onlyGaps, setOnlyGaps] = useState(true);

  const q = useQuery({
    queryKey: ["coverage", date ?? "today"],
    queryFn:  () => fetchCoverage(date ?? undefined),
    refetchInterval: 5 * 60_000,
  });

  const rows = useMemo(() => {
    const all = q.data?.publishers ?? [];
    return onlyGaps ? all.filter((p) => p.status !== "ok") : all;
  }, [q.data, onlyGaps]);

  const t = q.data ? totalsOf(q.data) : null;
  const pct = t && t.expected > 0 ? Math.round((t.produced / t.expected) * 100) : null;

  function toggle(id: number) {
    setOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  return (
    <div className="flex flex-col gap-4 animate-fade-up">
      <header className="flex items-end justify-between flex-wrap gap-2 pb-2 border-b" style={{ borderColor: "var(--rule)" }}>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">Crawler · Coverage</div>
          <h1 className="font-display text-[26px] leading-tight">
            What didn't arrive
            {q.data && <span className="font-mono text-[12px] text-[var(--fg-muted)] ml-3 align-middle">
              {shortDate(q.data.business_date)}
            </span>}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 font-mono text-[11px] text-[var(--fg-muted)] cursor-pointer">
            <input type="checkbox" checked={onlyGaps} onChange={(e) => setOnlyGaps(e.target.checked)} />
            gaps only
          </label>
          <button className="np-btn-ghost flex items-center gap-1 font-mono text-[11px]" onClick={() => q.refetch()} title="Refresh">
            <RefreshCw size={12} className={q.isFetching ? "animate-spin" : ""} /> refresh
          </button>
          {me?.group_name === "SuperAdmin" && (
            <Link to="/agent" className="np-btn flex items-center gap-1 font-mono text-[11px]" title="Open the operator agent">
              <Bot size={12} /> diagnose with agent
            </Link>
          )}
        </div>
      </header>

      {q.error && (
        <div className="np-banner flex items-center gap-2">
          <AlertTriangle size={14} className="text-[var(--accent)] shrink-0" />
          <span>{explainError(q.error)}</span>
        </div>
      )}
      {q.isLoading && <div className="py-8 flex justify-center"><Spinner label="Computing coverage" /></div>}

      {q.data && t && (
        <>
          {/* ── totals strip ─────────────────────────────────────── */}
          <section className="np-card px-4 py-3 flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <Stat label="expected" value={t.expected} />
            <Stat label="produced" value={t.produced} strong />
            <Stat label="missing" value={t.missing} accent={t.missing > 0} />
            <Stat label="coverage" value={pct !== null ? `${pct}%` : "—"} accent={pct !== null && pct < 90} />
            <span className="text-[var(--fg-faint)]">·</span>
            <Stat label="pubs ok" value={t.publishers_ok} />
            <Stat label="partial" value={t.publishers_partial} accent={t.publishers_partial > 0} />
            <Stat label="missing" value={t.publishers_missing} accent={t.publishers_missing > 0} />
            <Stat label="dead" value={t.publishers_dead} accent={t.publishers_dead > 0} />
            <Stat label="auth blocked" value={t.publishers_auth_blocked} accent={t.publishers_auth_blocked > 0} />
            <Stat label="off today" value={t.publishers_scheduled_off} />
            <Stat label="open alerts" value={t.open_alerts} accent={t.open_alerts > 0} />
            {Object.keys(t.by_classification).length > 0 && (
              <span className="font-mono text-[10px] text-[var(--fg-faint)] w-full">
                gaps by kind: {Object.entries(t.by_classification).map(([k, v]) => `${CLASS_LABEL[k] ?? k} ${v}`).join(" · ")}
              </span>
            )}
          </section>

          {q.data.notes.map((n, i) => (
            <div key={i} className="np-banner flex items-center gap-2">
              <AlertTriangle size={13} className="text-[var(--accent)] shrink-0" />
              <span className="font-sans text-[12px]">{n}</span>
            </div>
          ))}

          {/* ── publisher table ───────────────────────────────────── */}
          <section className="np-card overflow-hidden">
            <div className="grid grid-cols-[18px,1fr,60px,60px,60px,120px,100px,150px] gap-2 px-3 py-2 border-b font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)]"
                 style={{ borderColor: "var(--rule)" }}>
              <div /><div>Publisher</div><div className="text-right">Exp</div><div className="text-right">Got</div>
              <div className="text-right">Miss</div><div>Status</div><div>Last ok</div><div>Login session</div>
            </div>
            {rows.length === 0 && (
              <div className="px-4 py-6 text-center font-sans text-[13px] text-[var(--fg-muted)]">
                {onlyGaps ? "Every expected edition was produced." : "No publishers expected."}
              </div>
            )}
            <ul>
              {rows.map((p) => (
                <PublisherRow key={p.channel_id} p={p} open={open.has(p.channel_id)} onToggle={() => toggle(p.channel_id)} />
              ))}
            </ul>
          </section>

          {/* ── attempt errors + alerts ───────────────────────────── */}
          <div className="grid md:grid-cols-2 gap-4">
            <section className="np-card">
              <div className="px-3 py-2 border-b font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)]" style={{ borderColor: "var(--rule)" }}>
                Attempt errors for this date
              </div>
              {q.data.attempt_errors.length === 0 ? (
                <div className="px-3 py-4 font-sans text-[12px] text-[var(--fg-muted)]">
                  {q.data.approximate ? "Not available in browser-side mode." : "No failed attempts recorded."}
                </div>
              ) : (
                <ul>
                  {q.data.attempt_errors.map((e, i) => (
                    <li key={i} className="px-3 py-2 border-b last:border-b-0 font-mono text-[11px]" style={{ borderColor: "var(--rule)" }}>
                      <span className="text-[var(--fg)]">{e.channel_name ?? "?"}</span>
                      <span className="text-[var(--fg-faint)]"> · x{e.attempts} / {e.editions} ed · </span>
                      <span className="text-[var(--accent)]">{e.error_class ?? "error"}{e.http_status ? ` ${e.http_status}` : ""}</span>
                      {e.error_message && <div className="text-[var(--fg-muted)] truncate" title={e.error_message}>{e.error_message}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className="np-card">
              <div className="px-3 py-2 border-b font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)]" style={{ borderColor: "var(--rule)" }}>
                Open alerts (crawler.Alert_Crawler)
              </div>
              {q.data.open_alerts.length === 0 ? (
                <div className="px-3 py-4 font-sans text-[12px] text-[var(--fg-muted)]">
                  {q.data.approximate ? "Not available in browser-side mode." : "No unresolved alerts."}
                </div>
              ) : (
                <ul>
                  {q.data.open_alerts.map((a, i) => (
                    <li key={i} className="px-3 py-2 border-b last:border-b-0 font-mono text-[11px]" style={{ borderColor: "var(--rule)" }}>
                      <span className="np-chip mr-2" style={{ background: a.severity === "critical" ? "var(--accent)" : "var(--accent-soft)", color: a.severity === "critical" ? "var(--bg)" : "var(--accent)", borderColor: "transparent" }}>
                        {a.severity ?? "?"}
                      </span>
                      <span className="text-[var(--fg-muted)]">{a.alert_type} · ch{a.channel_id ?? "—"} · {a.created_at ? timeSince(a.created_at) : ""}</span>
                      <div className="text-[var(--fg)] truncate" title={a.title ?? ""}>{a.title}</div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, strong, accent }: { label: string; value: number | string; strong?: boolean; accent?: boolean }) {
  return (
    <span className="font-mono text-[12px] tabular-nums">
      <strong style={{ color: accent ? "var(--accent)" : strong ? "var(--fg)" : "var(--fg)" }}>{value}</strong>
      <span className="text-[var(--fg-faint)] ml-1 text-[10px] uppercase tracking-folio">{label}</span>
    </span>
  );
}

function PublisherRow({ p, open, onToggle }: { p: PublisherCoverage; open: boolean; onToggle: () => void }) {
  const auth = p.needs_login || p.auth_status
    ? `${p.auth_status ?? "unknown"}${p.auth_failure_count ? ` ×${p.auth_failure_count}` : ""}`
    : "—";
  return (
    <li className="border-b last:border-b-0" style={{ borderColor: "var(--rule)" }}>
      <button onClick={onToggle} className="w-full text-left grid grid-cols-[18px,1fr,60px,60px,60px,120px,100px,150px] gap-2 items-baseline px-3 py-2 hover:bg-[var(--bg-inset)]">
        <span className="text-[var(--fg-faint)]">{p.missing > 0 ? (open ? <ChevronDown size={12} /> : <ChevronRight size={12} />) : null}</span>
        <span className="min-w-0 flex items-baseline gap-2">
          <span className="font-display text-[15px] leading-none truncate">{p.channel_name}</span>
          <span className="font-mono text-[9px] text-[var(--fg-faint)]">ch{p.channel_id}</span>
          {p.no_publish_days && <span className="font-mono text-[9px] text-[var(--fg-faint)]">no {p.no_publish_days}</span>}
        </span>
        <span className="text-right font-mono text-[12px] tabular-nums">{p.expected}</span>
        <span className="text-right font-mono text-[12px] tabular-nums">{p.produced || <span className="text-[var(--fg-faint)]">—</span>}</span>
        <span className="text-right font-mono text-[12px] tabular-nums" style={{ color: p.missing > 0 ? "var(--accent)" : "var(--fg-faint)" }}>{p.missing || "—"}</span>
        <span><StatusChip status={p.status} /></span>
        <span className="font-mono text-[11px] text-[var(--fg-muted)] tabular-nums">
          {p.last_ok_date ? shortDate(p.last_ok_date) : "never"}
          {p.days_since_ok !== null && p.days_since_ok > 0 && <span className="text-[var(--fg-faint)]"> ({p.days_since_ok}d)</span>}
        </span>
        <span className="font-mono text-[11px]" style={{ color: p.auth_blocked ? "var(--accent)" : "var(--fg-muted)" }} title={p.auth_last_validated_at ? `validated ${p.auth_last_validated_at}` : ""}>
          {auth}
        </span>
      </button>
      {open && p.missing_editions.length > 0 && (
        <ul className="px-3 pb-2 pl-10 grid md:grid-cols-2 gap-x-6">
          {p.missing_editions.map((m) => <MissingRow key={m.edition_id} m={m} channelId={p.channel_id} />)}
        </ul>
      )}
    </li>
  );
}

function MissingRow({ m, channelId }: { m: MissingEdition; channelId: number }) {
  return (
    <li className="flex items-baseline gap-2 py-0.5 font-mono text-[11px] border-b last:border-b-0" style={{ borderColor: "var(--rule)" }}>
      <Link to={`/browse/${channelId}/${m.edition_id}`} className="np-link truncate min-w-0">{m.city ?? m.edition_name ?? `ed ${m.edition_id}`}</Link>
      <span className="text-[var(--fg-faint)]">#{m.edition_id}</span>
      <span className="ml-auto text-[var(--fg-muted)] whitespace-nowrap">{CLASS_LABEL[m.classification] ?? m.classification}</span>
      <span className="text-[var(--fg-faint)] whitespace-nowrap">{m.last_ok_date ? `last ${m.last_ok_date}` : "never"}</span>
      {m.review_reason && <span className="text-[var(--fg-faint)] truncate" title={m.review_reason}>· {m.review_reason}</span>}
    </li>
  );
}
